/* eslint-disable react/function-component-definition */
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { ASCENDING_KEY, DESCENDING_KEY, TIMESTAMP_KEY } from '../constants';
import TableDatepicker from '../../TableDatepicker';
import TooltipComponent from '../../TooltipComponent';
import {
  DEFAULT_VALUES_DAY_TIME_FORM,
  ICheckedTableItems,
  IS_DAY_TIME,
  ITripsOverviewTableData,
} from '../interfaces';
import {
  advancedFiltersAtom,
  checkedTableItemsAtom,
  hasMoreAtom,
  hiddenSummaryItemIndexesAtom,
  isActiveAdvanceFilterAtom,
  isModalFormOpenedAtom,
  offsetAtom,
  rideBookDataAtom,
  rideBookFilterAtom,
  rideItemIdsAtom,
  rideItemTimestampsAtom,
  sortFilterAtom,
  summaryItemIndexesAtom,
} from '../TripsOverviewState';
import useAnalytics from '../../../hooks/useAnalytics';

interface IThDatimeProps {
  id: string;
}

const ThDateTime = (props: IThDatimeProps): JSX.Element => {
  const setTableActionsFilters = useSetRecoilState(rideBookFilterAtom(props.id));
  const { formatMessage: f } = useIntl();
  const [sortFilter, setSortFilter] = useRecoilState<{ [key: string]: string }>(sortFilterAtom);
  const setCheckedTableItems = useSetRecoilState<ICheckedTableItems>(checkedTableItemsAtom);
  const setHasMore = useSetRecoilState<boolean>(hasMoreAtom);
  const setOffset = useSetRecoilState<number>(offsetAtom);
  const setRideItemIds = useSetRecoilState<string[]>(rideItemIdsAtom);
  const setSummaryItemIndexes = useSetRecoilState<number[]>(summaryItemIndexesAtom);
  const setHiddenSummaryItemIndexes = useSetRecoilState<number[]>(hiddenSummaryItemIndexesAtom);
  const setRideItemTimestamps = useSetRecoilState<string[]>(rideItemTimestampsAtom);
  const setTableData = useSetRecoilState<ITripsOverviewTableData | null>(rideBookDataAtom);
  const [, setIsModalFormOpened] = useRecoilState<string>(isModalFormOpenedAtom);
  const isActiveAdvanceFilter = useRecoilValue(isActiveAdvanceFilterAtom);
  const setDayTimeValue = useSetRecoilState(advancedFiltersAtom);
  const { trackEvent } = useAnalytics();

  const handleOnClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['ride book', 'click', 'Ride book date time filter']);

    setTableActionsFilters(
      (tableActionFilter) =>
        tableActionFilter && {
          ...tableActionFilter,
          id: props.id,
          isOpen: !tableActionFilter.isOpen,
          isActionChipsOpen: false,
        }
    );
  };

  const handleOnSort = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.stopPropagation();
    event.preventDefault();

    if (sortFilter[TIMESTAMP_KEY] === ASCENDING_KEY) {
      setSortFilter({ [TIMESTAMP_KEY]: DESCENDING_KEY });
      trackEvent(['ride book', 'click', 'Ride book sort by date time desc']);
    } else {
      setSortFilter({ [TIMESTAMP_KEY]: ASCENDING_KEY });
      trackEvent(['ride book', 'click', 'Ride book sort by date time asc']);
    }
    setTableData(null);
    setRideItemTimestamps([]);
    setSummaryItemIndexes([]);
    setHiddenSummaryItemIndexes([]);
    setRideItemIds([]);
    setOffset(0);
    setHasMore(true);
    setCheckedTableItems({
      mainCheckbox: false,
      intervals: [],
      intervalsBlacklist: [],
      rideIdBlacklist: {},
      rideIds: {},
      customerId: null,
      counts: {},
    });
  };

  const handleDayTimeModal = (event?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (event?.currentTarget.id) {
      trackEvent(['ride book', 'click', 'Ride book open date time modal']);
      if (!isActiveAdvanceFilter.days && !isActiveAdvanceFilter.time) {
        setDayTimeValue((oldState) => {
          return {
            ...oldState,
            ...DEFAULT_VALUES_DAY_TIME_FORM,
          };
        });
      }
      setIsModalFormOpened(IS_DAY_TIME);
    }
  };

  return (
    <div
      id="table-header-col-dateTime"
      className="th-sticky b-table-actions active"
      style={{
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        minWidth: '140px',
        flex: 1,
      }}
    >
      <TooltipComponent
        overlay={
          <div style={{ color: '#fff', whiteSpace: 'pre-wrap', fontWeight: 'normal' }}>
            {`${f({
              id: 'costsOverviewForm.dateTime',
            })}: `}
            <strong>
              {`${f({
                id: 'tables.tripsOverview.header.tooltip.timestamp',
              })}`}
            </strong>
          </div>
        }
        width="300px"
        style={{ top: '35px' }}
        positionLeft
      >
        <TableDatepicker
          // options={{ mode: 'range', menu: true }}
          name={props.id}
          type="head"
          isActive={true}
          isDayTime={true}
          onClickEvent={handleDayTimeModal}
        >
          <div
            id={props.id}
            className="d-flex"
            onClick={handleOnClick}
            style={{ position: 'relative' }}
            data-cy={`filter-button-text-${props.id}`}
          >
            <div className="text-truncate">{f({ id: 'costsOverviewForm.dateTime' })}</div>

            <a
              id={props.id}
              href="#"
              title="Datum a cas"
              className="filter cmd-icons cmd-icon-filter"
              data-cy={`filter-button-${props.id}`}
            >
              {/* test */}
            </a>
            <a
              id={props.id}
              href="#"
              title={
                sortFilter[TIMESTAMP_KEY] === ASCENDING_KEY
                  ? f({ id: 'table.filter.desc' })
                  : f({ id: 'table.filter.asc' })
              }
              className={`${sortFilter[TIMESTAMP_KEY] ? 'active' : 'inactive'} text-active cmd-icon cmd-icon-sorting${
                sortFilter[TIMESTAMP_KEY] === ASCENDING_KEY ? '-a-z' : '-z-a'
              } ${sortFilter[TIMESTAMP_KEY] ? 'text-active' : ''} text-decoration-none position-relative  ml-2`}
              onClick={handleOnSort}
              data-cy={`filter-buttons-sorting-btn-ridebook-${props.id}`}
            >
              {/* test */}
            </a>

            <div />
          </div>
        </TableDatepicker>
      </TooltipComponent>
    </div>
  );
};

export default ThDateTime;
